import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as MonitoringDocumentActions from "../../../actions/monitoringDocument";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import { NotificationAlert } from "../../../components/common/index";
import { TextFormInput } from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionMonitoringDocumentCreateBreadcrumbs, TrialInstitutionMonitoringDocumentDetailsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { MonitoringDocumentSubMenu } from "../../../helpers/subMenuHelper";
import { useNavigate } from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { isUrl } from "src/functions/string";

import "./MonitoringDocumentPage.scss";

interface MonitoringDocumentPageProps {
    form: Dtos.MonitoringDocument & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    monitoringDocument: Dtos.MonitoringDocument;
    lookups: Dtos.Lookup[];
    loadingMonitoringDocument: boolean;
    loadMonitoringDocumentSuccess: boolean;
    loadMonitoringDocumentFailure: boolean;
    loadMonitoringDocumentErrorCode: string;
    savingMonitoringDocument: boolean;
    saveMonitoringDocumentSuccess: boolean;
    saveMonitoringDocumentFailure: boolean;
    creatingMonitoringDocument: boolean;
    createMonitoringDocumentSuccess: boolean;
    createMonitoringDocumentFailure: boolean;
    validatingMonitoringDocument: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    pageSize: number;
    clearMonitoringDocument: MonitoringDocumentActions.IMonitoringDocumentClearActionCreator;
    loadMonitoringDocument: MonitoringDocumentActions.IMonitoringDocumentLoadByIdActionCreator;
    saveMonitoringDocument: MonitoringDocumentActions.IMonitoringDocumentSaveActionCreator;
    createMonitoringDocument: MonitoringDocumentActions.IMonitoringDocumentCreateActionCreator;
    validateMonitoringDocument: MonitoringDocumentActions.IMonitoringDocumentValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const reduxFormName: string = "reduxForms.monitoringDocument";

const MonitoringDocumentPage: FC<MonitoringDocumentPageProps> = ({
    form,
    formState,
    formProperties,
    monitoringDocument,
    lookups,
    loadingMonitoringDocument,
    //loadMonitoringDocumentSuccess,
    loadMonitoringDocumentFailure,
    loadMonitoringDocumentErrorCode,
    savingMonitoringDocument,
    saveMonitoringDocumentSuccess,
    saveMonitoringDocumentFailure,
    creatingMonitoringDocument,
    //createMonitoringDocumentSuccess,
    //createMonitoringDocumentFailure,
    validatingMonitoringDocument,
    validationFailures,
    permissions,
    trialInstitution,
    loadingTrialInstitution,
    //loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    clearMonitoringDocument,
    loadMonitoringDocument,
    saveMonitoringDocument,
    createMonitoringDocument,
    validateMonitoringDocument,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialInstitution,
    loadTrialInstitution,
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { monitoringId } = useParams<{ monitoringId: string }>();
    const { monitoringDocumentId } = useParams<{ monitoringDocumentId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);
    const monitoringIdNumber = parseInt(monitoringId, 10);
    const monitoringDocumentIdNumber = parseInt(monitoringDocumentId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.MonitoringDocument = {} as Dtos.MonitoringDocument;

        loadForm(
            reduxFormName,
            Object.assign({ ...monitoringDocument }, extraData)
        );
    };

    // Load/Create monitoring document
    useEffect(() => {
        if (monitoringDocumentIdNumber && monitoringIdNumber && trialInstitutionIdNumber && 
            trialIdNumber && !monitoringDocument && !loadingMonitoringDocument
        ) {
            setBreadcrumbs(
                TrialInstitutionMonitoringDocumentDetailsBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber, 
                    monitoringDocumentIdNumber
                )
            );

            loadMonitoringDocument(monitoringDocumentIdNumber);
        }
        else if (!monitoringDocumentIdNumber && monitoringIdNumber && trialInstitutionIdNumber && 
            trialIdNumber && !monitoringDocument && !creatingMonitoringDocument
        ) {
            setDrawerItems([]);
            setBreadcrumbs(
                TrialInstitutionMonitoringDocumentCreateBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber
                )
            );

            createMonitoringDocument(monitoringIdNumber);
        }
        else if (monitoringDocument && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }

        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        monitoringDocumentIdNumber,
        trialInstitutionIdNumber,
        trialIdNumber,
        monitoringDocument,
        loadingMonitoringDocument,
        creatingMonitoringDocument,
        formInitalised,
        trialInstitution,
        loadingTrialInstitution,
        monitoringIdNumber
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMonitoringDocumentFailure) {
            navigate(`/error/${loadMonitoringDocumentErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
    }, [
        loadMonitoringDocumentFailure,
        loadMonitoringDocumentErrorCode,
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode
    ]);

    // Update breadcrumbs & drawer items
    useEffect(() => {
        if (monitoringDocumentIdNumber && monitoringIdNumber && trialIdNumber && 
            trialInstitutionIdNumber && trialInstitution && permissions
        ) {
            setDrawerItems(
                MonitoringDocumentSubMenu(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber, 
                    monitoringDocumentIdNumber, 
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionMonitoringDocumentDetailsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    monitoringIdNumber,
                    monitoringDocumentIdNumber,
                    trialInstitution?.trial?.displayName,
                    trialInstitution?.institution?.name
                )
            );
        }
        else if (!monitoringDocumentIdNumber && monitoringIdNumber && 
            trialInstitutionIdNumber && trialIdNumber && trialInstitution
        ) {
            setDrawerItems([]);
            setBreadcrumbs(
                TrialInstitutionMonitoringDocumentCreateBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber,
                    trialInstitution?.trial?.displayName, 
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        monitoringDocumentIdNumber,
        monitoringIdNumber, 
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        monitoringDocument,
        permissions
    ]);

    // Reload monitoring Document after saving
    useEffect(() => {
        if (!savingMonitoringDocument && saveMonitoringDocumentSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Monitoring Document saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/documents`);
            } else if (!monitoringDocumentIdNumber) {
                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/documents/${monitoringDocument.id}/details`);
                clearMonitoringDocument();
                clearTrialInstitution();
            }
        }
    }, [
        savingMonitoringDocument,
        saveMonitoringDocumentSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingMonitoringDocument && saveMonitoringDocumentFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingMonitoringDocument, saveMonitoringDocumentFailure]);

     // Cleanup effect to clear the monitoring document & trial institution on component unmount
    useEffect(() => {
        return () => {
            clearMonitoringDocument();
            clearTrialInstitution();
            resetForm(reduxFormName);
        };
    }, [clearMonitoringDocument, clearTrialInstitution, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.MonitoringDocument = {} as Dtos.MonitoringDocument;

            if (!data.monitoringId) {
                changes.monitoringId = monitoringIdNumber;
            }

            await saveMonitoringDocument(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.MonitoringDocument) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/documents`);
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.MonitoringDocument = {} as Dtos.MonitoringDocument;

            if (!form.monitoringId) {
                changes.monitoringId = monitoringIdNumber;
            }

            if (form && hasBeenSubmitted && !validatingMonitoringDocument) {
                await validateMonitoringDocument(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const openFile = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const renderContent = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.MonitoringDocumentUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            const formDisabled: boolean =
                !hasPermission ||
                savingMonitoringDocument ||
                (formState && formState.formStatus == Dtos.FormStatus.Disabled);

            let formComponents: { [index: string]: RegistrationFormComponent } =
                {
                    details: {
                        type: RegistrationComponent.RegistrationElement,
                        component: (
                            <>
                                <h1>
                                    Details
                                </h1>
                                <div className="card card-shadow">
                                    <div className="form-row pt-4 pb-2 px-4">
                                        <div className="col-12">
                                            {renderFormControls(
                                                form,
                                                "monitoringDocument",
                                                {
                                                    name: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: TextFormInput,
                                                        inputDisabled: formDisabled,
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-2",
                                                        fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row pt-2 pb-4 px-4">
                                        <div className="col-12">
                                            {renderFormControls(
                                                form,
                                                "monitoringDocument",
                                                {
                                                    location: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: TextFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            addOnRightButton: (
                                                                <button
                                                                    className="btn btn-secondary"
                                                                    id="button-addon2"
                                                                    type="button"
                                                                    style={{
                                                                        padding: "4px 10px",
                                                                        cursor: form?.location == null || !isUrl(form?.location)
                                                                                ? "not-allowed"
                                                                                : "pointer",
                                                                    }}
                                                                    disabled={form?.location == null || !isUrl(form?.location)}
                                                                    onClick={() => {
                                                                        openFile(form.location);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                                </button>
                                                            ),
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-2",
                                                        fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ),
                    },
                };

            return (
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "monitoringDocument",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            );
        }
        return null;
    }, [
        permissions,
        lookups,
        form,
        permissions,
        onCancel,
        handleOnSubmit,
        handleOnSubmitFailed,
        loadingMonitoringDocument,
        savingMonitoringDocument,
        creatingMonitoringDocument,
        formState,
        monitoringDocument
    ]);

    return (
        <RestrictedLayout
            loading={
                loadingMonitoringDocument || 
                creatingMonitoringDocument ||
                loadingTrialInstitution
            }
            permission={Dtos.Permission.MonitoringDocumentView}
        >
            { renderContent }
        </RestrictedLayout>
    )
};

const mapStateToProps = (state: ITmdState): Partial<MonitoringDocumentPageProps> => {
    let monitoringDocument: Dtos.MonitoringDocument | undefined = !(state.monitoringDocument.formData instanceof Array)
        ? state.monitoringDocument.formData
        : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined = !(state.trialInstitution.formData instanceof Array)
        ? state.trialInstitution.formData
        : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {
        monitoringDocument: monitoringDocument!,
        loadingMonitoringDocument: state.monitoringDocument.loadState && state.monitoringDocument.loadState.status == RequestState.Pending,
        loadMonitoringDocumentSuccess: state.monitoringDocument.loadState && state.monitoringDocument.loadState.status == RequestState.Success,
        loadMonitoringDocumentFailure: state.monitoringDocument.loadState && state.monitoringDocument.loadState.status == RequestState.Failure,
        loadMonitoringDocumentErrorCode: state.monitoringDocument.loadState.errorCode,
        savingMonitoringDocument: state.monitoringDocument.saveState && state.monitoringDocument.saveState.status == RequestState.Pending,
        saveMonitoringDocumentSuccess: state.monitoringDocument.saveState && state.monitoringDocument.saveState.status == RequestState.Success,
        saveMonitoringDocumentFailure: state.monitoringDocument.saveState && state.monitoringDocument.saveState.status == RequestState.Failure,
        creatingMonitoringDocument: state.monitoringDocument.createState && state.monitoringDocument.createState.status == RequestState.Pending,
        createMonitoringDocumentSuccess: state.monitoringDocument.createState && state.monitoringDocument.createState.status == RequestState.Success,
        createMonitoringDocumentFailure: state.monitoringDocument.createState && state.monitoringDocument.createState.status == RequestState.Failure,
        validatingMonitoringDocument: state.monitoringDocument.validateState && state.monitoringDocument.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.monitoringDocument.lookups,
        form: state.reduxForms.monitoringDocument,
        formState: state.monitoringDocument.formState,
        formProperties: state.monitoringDocument.formProperties,
        validationFailures: monitoringDocument
            ? state.monitoringDocument.validationFailures
            : undefined,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<MonitoringDocumentPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadMonitoringDocument: bindActionCreators(MonitoringDocumentActions.LoadMonitoringDocumentById, dispatch),
        clearMonitoringDocument: bindActionCreators(MonitoringDocumentActions.Clear, dispatch),
        saveMonitoringDocument: bindActionCreators(MonitoringDocumentActions.SaveMonitoringDocument, dispatch),
        createMonitoringDocument: bindActionCreators(MonitoringDocumentActions.CreateMonitoringDocument, dispatch),
        validateMonitoringDocument: bindActionCreators(MonitoringDocumentActions.ValidateMonitoringDocument, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringDocumentPage);